import {
  ProjectParticipationsDataT,
  ProjectParticipationsT,
} from '@/components/common/menu/lib/types/navigationDataTypes.ts'
import { api } from '@/lib/api-client.ts'
import { useInfiniteQuery } from '@tanstack/react-query'

export type GetProjectsParticipationsReturnT = {
  projectParticipations: ProjectParticipationsT
  userParticipationsCount: number
  nextPage: number | null
  currentPage: number
}

export async function getUserProjectParticipations(
  page: number,
): Promise<GetProjectsParticipationsReturnT | undefined> {
  // get project stages accessible for the currently logged user
  const response = await api.get(`/users/me/project_participations`)
  const data: ProjectParticipationsDataT = response.data
  const projectParticipations: ProjectParticipationsT = data[`hydra:member`] || []
  const userParticipationsCount: number = response.data[`hydra:totalItems`] ?? 0
  const hasNextPage =
    page * projectParticipations?.length < userParticipationsCount &&
    projectParticipations?.length > 0
  return {
    projectParticipations,
    userParticipationsCount,
    nextPage: hasNextPage ? page + 1 : null,
    currentPage: page,
  }
}

export const userProjectParticipationsQuery = () => ({
  queryKey: ['userProjectParticipations'],
  queryFn: ({ pageParam = 1 }) => getUserProjectParticipations(pageParam),
  getNextPageParam: (lastPage: unknown) => (lastPage as { nextPage: number | null })?.nextPage,
  initialPageParam: 1,
  staleTime: 1000 * 60,
})

export function useGetUserProjectParticipations() {
  const {
    data: userProjectParticipations,
    hasNextPage: userProjectsHasNextPage,
    isFetchingNextPage: userProjectsIsFetchingNextPage,
    fetchNextPage: userProjectsFetchNextPage,
    isPending: userProjectsIsPending,
    isError: userProjectsIsError,
  } = useInfiniteQuery({ ...userProjectParticipationsQuery() })

  const projectParticipationsFlat =
    userProjectParticipations?.pages.flatMap(page => page?.projectParticipations ?? []) ?? []
  //

  // `projectParticipationsFlat` is `[]` in three different situations — still
  // loading, failed, and genuinely no stages — and callers that seed a form
  // field from its length need to tell them apart. An empty list read as "this
  // person is on no stage" is the exact defect this list was pulled in to fix,
  // so the two states are exported rather than left to be inferred.
  return {
    projectParticipationsFlat,
    userProjectsHasNextPage,
    userProjectsIsFetchingNextPage,
    userProjectsFetchNextPage,
    userProjectsIsPending,
    userProjectsIsError,
    hasParticipations: projectParticipationsFlat.length > 0,
  }
}
